// Cross-platform report download/export.
//
// WEB: preserves the existing, working browser download (Blob + <a download>).
// NATIVE (iOS/Android via Capacitor): the browser download does NOT produce a file the user
// can find, so we fetch the bytes (authenticated), write them to app-private cache storage,
// then present the OS Share/Save sheet (Save to Files, AirDrop, Mail, etc). Success is only
// reported once the sheet is presented — never merely because an HTTP request returned 200.
import { Capacitor } from "@capacitor/core";
import { rawApi, BACKEND } from "./api";

function webDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error("Could not read file"));
    r.onload = () => resolve(String(r.result || "").split(",")[1] || "");
    r.readAsDataURL(blob);
  });
}

// Fetch the report bytes as a Blob. Native uses the raw axios client (Bearer/native HTTP stack);
// web uses cookie-authenticated fetch. `path` is relative to the API root (e.g. "/account/export").
async function fetchBlob(path, mimeType) {
  if (Capacitor.isNativePlatform()) {
    const res = await rawApi.get(path, { responseType: "blob" });
    const d = res.data;
    return d instanceof Blob ? d : new Blob([d], { type: mimeType });
  }
  const res = await fetch(`${BACKEND}/api${path}`, { credentials: "include" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return await res.blob();
}

// Save (web) or present the native Share/Save sheet (native) for an already-fetched Blob.
// Returns { outcome: "downloaded" | "shared" | "cancelled" }.
export async function saveOrShareBlob(blob, filename, mimeType) {
  if (!blob || blob.size === 0) throw new Error("The generated file was empty.");
  if (!Capacitor.isNativePlatform()) {
    webDownload(blob, filename);
    return { outcome: "downloaded" };
  }
  const { Filesystem, Directory } = await import("@capacitor/filesystem");
  const { Share } = await import("@capacitor/share");
  const data = await blobToBase64(blob);
  if (!data) throw new Error("The generated file was empty.");
  await Filesystem.writeFile({ path: filename, data, directory: Directory.Cache });
  const { uri } = await Filesystem.getUri({ path: filename, directory: Directory.Cache });
  try {
    await Share.share({ title: filename, url: uri, dialogTitle: "Save or share" });
    return { outcome: "shared" };
  } catch (e) {
    const msg = String((e && e.message) || "").toLowerCase();
    if (msg.includes("cancel") || msg.includes("abort") || msg.includes("dismiss")) {
      return { outcome: "cancelled" };
    }
    throw e;
  }
}

// Fetch a report and then save/share it. Returns the same outcome shape as saveOrShareBlob.
export async function downloadReport({ path, filename, mimeType }) {
  const blob = await fetchBlob(path, mimeType);
  return await saveOrShareBlob(blob, filename, mimeType);
}
